import { useCallback, useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { Template, TemplateConfigurationWithResolvedRefs } from '@klareospace/pharos-lib-types';

import { useShipperConfigurations } from './use-shipper-configurations';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_CONFIGURATIONS: TemplateConfigurationWithResolvedRefs[] = [];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseShippersPageReturn {
  closeCatalog: () => void;
  closeInstallDrawer: () => void;
  configurations: TemplateConfigurationWithResolvedRefs[];
  currentPage: number;
  isCatalogOpen: boolean;
  isInstallDrawerOpen: boolean;
  isLoadingConfigurations: boolean;
  onInstallTemplate: (template: Template) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  openCatalog: () => void;
  pageSize: number;
  /** Template id for the install drawer — set when a catalog tile is clicked. */
  pendingInstallTemplateId: string | null;
  searchValue: string;
  totalItems: number;
  totalPages: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useShippersPage(): UseShippersPageReturn {
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [isInstallDrawerOpen, setIsInstallDrawerOpen] = useState(false);
  const [pendingInstallTemplateId, setPendingInstallTemplateId] = useState<string | null>(null);

  const searchState = useSearchPageState();

  const { data, isLoading: isLoadingConfigurations } = useShipperConfigurations({
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch,
  });

  // Pre-compute derived values to keep the return statement branch-free.
  const configurations = data ? data.items : EMPTY_CONFIGURATIONS;
  const totalItems = data ? data.total : 0;
  const totalPages = data ? data.totalPages : 1;

  const openCatalog = useCallback((): void => setIsCatalogOpen(true), []);
  const closeCatalog = useCallback((): void => setIsCatalogOpen(false), []);

  const closeInstallDrawer = useCallback((): void => {
    setIsInstallDrawerOpen(false);
    setPendingInstallTemplateId(null);
  }, []);

  /**
   * Opens the install drawer for a specific shipper template selected from the
   * catalog. The template's id is used to resolve form fields.
   */
  const onInstallTemplate = useCallback((template: Template): void => {
    setPendingInstallTemplateId(template.id);
    setIsInstallDrawerOpen(true);
  }, []);

  return {
    closeCatalog,
    closeInstallDrawer,
    configurations,
    currentPage: searchState.currentPage,
    isCatalogOpen,
    isInstallDrawerOpen,
    isLoadingConfigurations,
    onInstallTemplate,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    openCatalog,
    pageSize: searchState.pageSize,
    pendingInstallTemplateId,
    searchValue: searchState.searchValue,
    totalItems,
    totalPages,
  };
}
